/**
 * 朝堂系统状态管理
 */

import { create } from 'zustand'
import type {
  CourtState,
  GovernmentPosition,
  Official,
  CouncilSession,
  NationalPolicy,
  GovernmentSystem
} from '../types/court'

// 初始状态
const initialState: CourtState = {
  governmentSystem: 'three_dukes',
  positions: new Map(),
  officials: new Map(),
  councilSessions: new Map(),
  policies: new Map(),
  positionHistories: new Map(),
  officialPerformances: new Map(),
  factions: [],
  statistics: {
    totalCouncils: 0,
    passedCouncils: 0,
    failedCouncils: 0,
    averageOfficialPerformance: 0,
    totalPolicies: 0,
    activePolicies: 0
  }
}

// 预设官职体系 - 三公九卿制
const THREE_DUKES_POSITIONS: GovernmentPosition[] = [
  {
    id: 'prime_minister',
    name: '丞相',
    system: 'three_dukes',
    rank: 'first_rank',
    tier: 1,
    permissions: {
      admin: true,
      financial: true
    },
    responsibilities: ['辅佐君主', '管理政务', '推荐官员'],
    salary: { base: 1000 },
    requiredSkills: {
      administration: 80,
      intelligence: 70,
      loyalty: 80
    },
    hierarchy: {
      subordinates: ['grand commandant', 'censor_in_chief']
    }
  },
  {
    id: 'grand_commandant',
    name: '太尉',
    system: 'three_dukes',
    rank: 'first_rank',
    tier: 1,
    permissions: {
      military: true
    },
    responsibilities: ['掌管军事', '指挥军队', '训练士兵'],
    salary: { base: 1000 },
    requiredSkills: {
      military: 80,
      leadership: 70,
      loyalty: 80
    },
    hierarchy: {
      superior: 'prime_minister'
    }
  },
  {
    id: 'censor_in_chief',
    name: '御史大夫',
    system: 'three_dukes',
    rank: 'first_rank',
    tier: 1,
    permissions: {
      judicial: true,
      admin: true
    },
    responsibilities: ['监察百官', '弹劾不法', '掌管文书'],
    salary: { base: 1000 },
    requiredSkills: {
      intelligence: 70,
      integrity: 80,
      loyalty: 80
    },
    hierarchy: {
      superior: 'prime_minister'
    }
  },
  {
    id: 'minister_of_agriculture',
    name: '大司农',
    system: 'three_dukes',
    rank: 'second_rank',
    tier: 2,
    permissions: {
      financial: true
    },
    responsibilities: ['掌管财政', '管理税收', '调配物资'],
    salary: { base: 800 },
    requiredSkills: {
      administration: 70,
      intelligence: 60
    },
    hierarchy: {
      superior: 'prime_minister'
    }
  },
  {
    id: 'minister_of_rites',
    name: '太常',
    system: 'three_dukes',
    rank: 'second_rank',
    tier: 2,
    permissions: {
      cultural: true
    },
    responsibilities: ['掌管礼仪', '祭祀宗庙', '教育贵族'],
    salary: { base: 800 },
    requiredSkills: {
      cultural: 70,
      integrity: 60
    },
    hierarchy: {
      superior: 'prime_minister'
    }
  },
  {
    id: 'minister_of_justice',
    name: '廷尉',
    system: 'three_dukes',
    rank: 'second_rank',
    tier: 2,
    permissions: {
      judicial: true
    },
    responsibilities: ['掌管司法', '审理案件', '执行刑罚'],
    salary: { base: 800 },
    requiredSkills: {
      intelligence: 70,
      integrity: 70
    },
    hierarchy: {
      superior: 'censor_in_chief'
    }
  }
]

// 初始化官职
function initializePositions() {
  const positions = new Map<string, GovernmentPosition>()
  THREE_DUKES_POSITIONS.forEach(position => {
    positions.set(position.id, position)
  })
  return positions
}

initialState.positions = initializePositions()

interface CourtStore extends CourtState {
  // 官职体系管理
  setGovernmentSystem: (system: GovernmentSystem) => void
  addPosition: (position: GovernmentPosition) => void
  
  // 官员管理
  appointOfficial: (characterId: string, positionId: string, factionId: string) => string
  dismissOfficial: (positionId: string, reason: string) => void
  getOfficial: (positionId: string) => Official | null
  
  // 朝议管理
  createCouncilSession: (session: Omit<CouncilSession, 'id' | 'proposedAt' | 'status'>) => string
  castVote: (sessionId: string, characterId: string, vote: 'yes' | 'no' | 'abstain', reason?: string) => void
  concludeSession: (sessionId: string) => void
  
  // 国策管理
  enactPolicy: (policy: Omit<NationalPolicy, 'id' | 'enactedAt' | 'status'>) => string
  repealPolicy: (policyId: string, reason: string) => void
  
  // 派系管理
  createFaction: (name: string, leader: string, members: string[]) => string
  addFactionMember: (factionId: string, characterId: string) => void
  
  // 统计
  updateStatistics: () => void
}

export const useCourtStore = create<CourtStore>((set, get) => ({
  ...initialState,
  
  // 官职体系管理
  setGovernmentSystem: (system) => {
    set({ governmentSystem: system })
  },
  
  addPosition: (position) => {
    set(state => {
      const newPositions = new Map(state.positions)
      newPositions.set(position.id, position)
      return { positions: newPositions }
    })
  },
  
  // 官员管理
  appointOfficial: (characterId, positionId, factionId) => {
    const id = `official_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newOfficials = new Map(state.officials)
      newOfficials.set(positionId, {
        id,
        characterId,
        positionId,
        factionId,
        term: {
          startedAt: now
        },
        status: 'active',
        performance: {
          rating: 50,
          achievements: [],
          failures: [],
          tenure: 0
        },
        influence: 50,
        corruption: 0
      })
      
      return { officials: newOfficials }
    })
    
    return id
  },
  
  dismissOfficial: (positionId, _reason) => {
    const now = { year: 1046, month: 1, day: 1 }
    set(state => {
      const newOfficials = new Map(state.officials)
      const official = newOfficials.get(positionId)
      
      if (official) {
        newOfficials.set(positionId, {
          ...official,
          status: 'dismissed',
          term: {
            ...official.term,
            endedAt: now
          }
        })
      }
      
      return { officials: newOfficials }
    })
  },
  
  getOfficial: (positionId) => {
    return get().officials.get(positionId) || null
  },
  
  // 朝议管理
  createCouncilSession: (session) => {
    const id = `council_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newSessions = new Map(state.councilSessions)
      newSessions.set(id, {
        ...session,
        id,
        proposedAt: now,
        status: 'proposed',
        votes: []
      })
      
      return {
        councilSessions: newSessions,
        statistics: {
          ...state.statistics,
          totalCouncils: state.statistics.totalCouncils + 1
        }
      }
    })
    
    return id
  },
  
  castVote: (sessionId, characterId, vote, reason) => {
    set(state => {
      const newSessions = new Map(state.councilSessions)
      const session = newSessions.get(sessionId)
      
      if (session) {
        const existingVoteIndex = session.votes.findIndex(v => v.characterId === characterId)
        const newVotes = [...session.votes]
        
        if (existingVoteIndex >= 0) {
          newVotes[existingVoteIndex] = { characterId, vote, reason }
        } else {
          newVotes.push({ characterId, vote, reason })
        }
        
        newSessions.set(sessionId, {
          ...session,
          votes: newVotes,
          status: 'voting'
        })
      }
      
      return { councilSessions: newSessions }
    })
  },
  
  concludeSession: (sessionId) => {
    const now = { year: 1046, month: 1, day: 1 }
    set(state => {
      const newSessions = new Map(state.councilSessions)
      const session = newSessions.get(sessionId)
      
      if (session) {
        const yesVotes = session.votes.filter(v => v.vote === 'yes').length
        const noVotes = session.votes.filter(v => v.vote === 'no').length
        const result = yesVotes > noVotes ? 'passed' : 'rejected'
        
        newSessions.set(sessionId, {
          ...session,
          result,
          decisionAt: now,
          status: result === 'passed' ? 'executing' : 'rejected'
        })
        
        // 更新统计
        let passedCouncils = state.statistics.passedCouncils
        let failedCouncils = state.statistics.failedCouncils
        
        if (result === 'passed') {
          passedCouncils++
        } else {
          failedCouncils++
        }
        
        return {
          councilSessions: newSessions,
          statistics: {
            ...state.statistics,
            passedCouncils,
            failedCouncils
          }
        }
      }
      
      return state
    })
  },
  
  // 国策管理
  enactPolicy: (policy) => {
    const id = `policy_${Date.now()}`
    const now = { year: 1046, month: 1, day: 1 }
    
    set(state => {
      const newPolicies = new Map(state.policies)
      newPolicies.set(id, {
        ...policy,
        id,
        enactedAt: now,
        status: 'active'
      })
      
      return {
        policies: newPolicies,
        statistics: {
          ...state.statistics,
          totalPolicies: state.statistics.totalPolicies + 1,
          activePolicies: state.statistics.activePolicies + 1
        }
      }
    })
    
    return id
  },
  
  repealPolicy: (policyId, _reason) => {
    set(state => {
      const newPolicies = new Map(state.policies)
      const policy = newPolicies.get(policyId)
      
      if (policy) {
        newPolicies.set(policyId, {
          ...policy,
          status: 'inactive'
        })
        
        return {
          policies: newPolicies,
          statistics: {
            ...state.statistics,
            activePolicies: Math.max(0, state.statistics.activePolicies - 1)
          }
        }
      }
      
      return state
    })
  },
  
  // 派系管理
  createFaction: (name, leader, members) => {
    const id = `faction_${Date.now()}`
    
    set(state => ({
      factions: [
        ...state.factions,
        {
          id,
          name,
          leader,
          members,
          influence: 50,
          agenda: []
        }
      ]
    }))
    
    return id
  },
  
  addFactionMember: (factionId, characterId) => {
    set(state => ({
      factions: state.factions.map(faction => 
        faction.id === factionId 
          ? { ...faction, members: [...faction.members, characterId] }
          : faction
      )
    }))
  },
  
  // 统计
  updateStatistics: () => {
    const state = get()
    const officials = Array.from(state.officials.values())
    const averagePerformance = officials.length > 0 
      ? officials.reduce((sum, official) => sum + official.performance.rating, 0) / officials.length
      : 0
    
    set({
      statistics: {
        ...state.statistics,
        averageOfficialPerformance: Math.round(averagePerformance)
      }
    })
  }
}))
